import { useId, useState, type ChangeEvent } from 'react';

import { basisPointsToPercentText, isPercentText, percentTextToBasisPoints } from './percent';
import { useFieldWiring } from './useFieldWiring';

export interface PercentInputProps {
  /** The rate in basis points (825 is 8.25%), or null while the field is empty. */
  readonly value: number | null;
  /** Called with whole basis points, or null when the field is cleared. */
  readonly onChange: (basisPoints: number | null) => void;
  /** Hint text shown in the empty control. */
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** Accessible name, required when the input is used outside a FormField. */
  readonly 'aria-label'?: string;
}

/**
 * A percentage, entered and shown as a percentage, held as integer basis points.
 *
 * People write "8.25"; the API wants 825. Making every form do that conversion
 * is how the same rate ends up shown as "825", "8.25%", "8.3%" and "0.0825" in
 * different corners of the product. This does it once: the field reads and
 * shows percentages with up to two decimals (and accepts a comma as the
 * separator), and its `value` and `onChange` are basis points, so nothing
 * outside it ever holds a float.
 *
 * Typing is constrained rather than validated afterwards: a third decimal or a
 * letter simply does not appear. Rates are not negative here, so a minus sign
 * does not appear either, and a `value` below zero (or above 9999.99%) shows as
 * an empty field rather than as text that could not be typed again.
 *
 * Wire it like any control, inside a {@link FormField}. The unit is drawn after
 * the number and also spoken as part of the field's description, because a
 * decoration that a screen reader skips would leave "8.25" unexplained.
 *
 * @param props see {@link PercentInputProps}
 * @returns the input
 */
export function PercentInput({
  value,
  onChange,
  placeholder,
  disabled = false,
  'aria-label': ariaLabel,
}: PercentInputProps): React.JSX.Element {
  const { controlId, describedBy, invalid, required } = useFieldWiring();
  const unitId = useId();
  const [text, setText] = useState(() => basisPointsToPercentText(value));
  const [seen, setSeen] = useState(value);

  // The value can also change from outside, such as a form reset or a loaded record. Follow it,
  // unless the text already means it: "8." must not be rewritten to "8" mid-keystroke.
  if (value !== seen) {
    setSeen(value);
    if (percentTextToBasisPoints(text) !== value) {
      setText(basisPointsToPercentText(value));
    }
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    const next = event.target.value;
    if (!isPercentText(next)) {
      return;
    }
    const basisPoints = percentTextToBasisPoints(next);
    setText(next);
    setSeen(basisPoints);
    onChange(basisPoints);
  }

  return (
    <span className="fo-input" data-invalid={invalid ? '' : undefined}>
      <input
        id={controlId}
        className="fo-input__control"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-describedby={[describedBy, unitId].filter(Boolean).join(' ')}
        onChange={handleChange}
        onBlur={() => {
          setText(basisPointsToPercentText(value));
        }}
      />
      <span className="fo-input__suffix" aria-hidden="true">
        %
      </span>
      <span id={unitId} className="visually-hidden">
        percent
      </span>
    </span>
  );
}
